import React, { useEffect, useState } from "react";
import Footer from "../../components/Footer/Footer";
import Header from "../../components/Header/Header";
import { useDispatch, useSelector } from "react-redux";
import { postComplaint } from "../../redux/features/complaint";
import useAuth from "../../hooks/useAuth";
import { FaRegCheckCircle } from "react-icons/fa";

const STEPS = [
    { label: "Details" },
    { label: "Explanation" },
    { label: "Review" },
];

const glassStyles = {
    background: "rgba(255,255,255,0.6)",
    boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
    backdropFilter: "blur(16px)",
    borderRadius: "1rem",
    border: "1px solid rgba(255,255,255,0.25)",
};

const Complain = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        link: "",
        reason: [],
        message: "",
    });
    const [currentStep, setCurrentStep] = useState(1);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [sucessMessage, setSuccessMessage] = useState("");
    const [errors, setErrors] = useState({});
    const dispatch = useDispatch();
    const { token } = useAuth();
    const { isLoading } = useSelector((state) => state.complaints);

    // ✅ Validation
    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (!formData.email.trim()) newErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.email))
            newErrors.email = "Enter a valid email";
        if (!formData.link.trim()) newErrors.link = "Link is required";
        else if (!/^https?:\/\/.+/.test(formData.link))
            newErrors.link = "Enter a valid URL";
        if (formData.reason.length === 0)
            newErrors.reason = "Select at least one reason";
        if (!formData.message.trim() || formData.message.length < 2)
            newErrors.message = "Please provide details (min 2 chars)";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ✅ API submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (validateForm()) {
                const res = await dispatch(postComplaint({ token, formData })).unwrap();
                if (res.status) {
                    setSuccessMessage(res.response);
                    setShowSuccessModal(true);
                    setTimeout(() => (window.location.href = "/"), 3000);
                }
            } else {
                if ("name" in errors || "email" in errors) setCurrentStep(1);
                else if ("link" in errors || "reason" in errors || "message" in errors)
                    setCurrentStep(2);
            }
        } catch (error) { }
    };

    const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, 3));
    const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // ✅ Blue Stepper
    const Stepper = () => (
        <div className="flex items-center justify-between max-w-md mx-auto mb-10">
            {STEPS.map((step, idx) => {
                const isActive = currentStep === idx + 1;
                const isComplete = currentStep > idx + 1;
                return (
                    <div className="flex flex-col items-center w-1/3" key={step.label}>
                        <div
                            className={`transition-all w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium 
                ${isActive
                                    ? "bg-gradient-to-r from-blue-600 to-blue-500 text-white scale-110 shadow-md"
                                    : isComplete
                                        ? "bg-green-500 text-white"
                                        : "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-300"
                                }`}
                        >
                            {isComplete ? <FaRegCheckCircle /> : idx + 1}
                        </div>
                        <span
                            className={`mt-2 text-xs ${isActive
                                ? "text-blue-600 dark:text-blue-400 font-semibold"
                                : "text-gray-500 dark:text-gray-400"
                                }`}
                        >
                            {step.label}
                        </span>
                        {idx !== STEPS.length - 1 && (
                            <div
                                className={`w-16 h-0.5 ${currentStep > idx + 1 ? "bg-blue-500 dark:bg-blue-400" : "bg-gray-200 dark:bg-gray-600"
                                    }`}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );

    return (
        <>
            <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 font-sans transition-colors duration-300">
                <Header />
                <main className="max-w-3xl mx-auto px-6 py-12 pt-[100px]">
                    {/* Heading */}
                    <div className="text-center mb-10">
                        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-400 dark:to-blue-300 bg-clip-text text-transparent mb-2">
                            Appeal a Decision
                        </h1>
                        <p className="text-base text-gray-600 dark:text-gray-300">
                            If you believe a decision was made in error, submit an appeal.
                            We’ll respond within 3-5 business days.
                        </p>
                    </div>

                    <Stepper />

                    {/* Form */}
                    <div className="bg-white/90 dark:bg-slate-800/80 rounded-lg shadow-md p-6 backdrop-blur-sm border border-gray-100 dark:border-gray-700 transition-colors">
                        <form onSubmit={handleSubmit}>
                            {/* Step 1 */}
                            {currentStep === 1 && (
                                <div className="space-y-6">
                                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                                        Personal Information
                                    </h2>
                                    <div>
                                        <label className="text-sm mb-1 block text-gray-700 dark:text-gray-300">
                                            Full Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) =>
                                                setFormData({ ...formData, name: e.target.value })
                                            }
                                            className="w-full px-4 py-2.5 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-slate-700 focus:ring-2 focus:ring-blue-500 outline-none transition text-gray-800 dark:text-gray-100"
                                            placeholder="John Doe"
                                        />
                                        {errors.name && (
                                            <p className="text-xs text-red-600 mt-1">{errors.name}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm mb-1 block text-gray-700 dark:text-gray-300">
                                            Email <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) =>
                                                setFormData({ ...formData, email: e.target.value })
                                            }
                                            className="w-full px-4 py-2.5 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-slate-700 focus:ring-2 focus:ring-blue-500 outline-none text-gray-800 dark:text-gray-100"
                                            placeholder="name@email.com"
                                        />
                                        {errors.email && (
                                            <p className="text-xs text-red-600">{errors.email}</p>
                                        )}
                                    </div>
                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={nextStep}
                                            disabled={!formData.name || !formData.email}
                                            className={`px-6 py-2 rounded-full text-sm font-medium text-white 
                        ${!formData.name || !formData.email
                                                    ? "bg-gray-300 dark:bg-gray-600 cursor-not-allowed"
                                                    : "bg-gradient-to-r from-blue-600 to-blue-500 hover:opacity-90 transition"
                                                }`}
                                        >
                                            Next →
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 2 */}
                            {currentStep === 2 && (
                                <div className="space-y-6">
                                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                                        Report Details
                                    </h2>
                                    <div>
                                        <label className="text-sm mb-1 block text-gray-700 dark:text-gray-300">
                                            Link to Report <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="url"
                                            value={formData.link}
                                            onChange={(e) =>
                                                setFormData({ ...formData, link: e.target.value })
                                            }
                                            className="w-full px-4 py-2.5 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-slate-700 focus:ring-2 focus:ring-blue-500 outline-none text-gray-800 dark:text-gray-100"
                                            placeholder="Enter the URL"
                                        />
                                        {errors.link && (
                                            <p className="text-xs text-red-600 mt-1">{errors.link}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm mb-2 block text-gray-700 dark:text-gray-300">
                                            Reason for Report <span className="text-red-500">*</span>
                                        </label>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                            {[
                                                "Malware",
                                                "Spyware",
                                                "Phishing",
                                                "Scam",
                                                "Inappropriate Content",
                                                "Other",
                                            ].map((option) => (
                                                <div key={option} className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        id={option}
                                                        value={option}
                                                        checked={formData.reason?.includes(option)}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                reason: e.target.checked
                                                                    ? [...prev.reason, value]
                                                                    : prev.reason.filter((r) => r !== value),
                                                            }));
                                                        }}
                                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                                                    />
                                                    <label
                                                        htmlFor={option}
                                                        className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                                                    >
                                                        {option}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                        {errors.reason && (
                                            <p className="text-xs text-red-600 mt-1">{errors.reason}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm mb-1 block text-gray-700 dark:text-gray-300">
                                            Additional Details <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            value={formData.message}
                                            onChange={(e) =>
                                                setFormData({ ...formData, message: e.target.value })
                                            }
                                            rows={4}
                                            className="w-full px-4 py-2.5 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-slate-700 focus:ring-2 focus:ring-blue-500 outline-none text-gray-800 dark:text-gray-100"
                                            placeholder="Please provide additional details..."
                                        />
                                        {errors.message && (
                                            <p className="text-xs text-red-600 mt-1">{errors.message}</p>
                                        )}
                                    </div>
                                    <div className="flex justify-between">
                                        <button
                                            type="button"
                                            onClick={prevStep}
                                            className="px-5 py-2 rounded-full text-sm font-medium text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                                        >
                                            ← Previous
                                        </button>
                                        <button
                                            type="button"
                                            onClick={nextStep}
                                            className="px-6 py-2 rounded-full text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:opacity-90 transition"
                                        >
                                            Next →
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 3 */}
                            {currentStep === 3 && (
                                <div className="space-y-6">
                                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                                        Review and Submit
                                    </h2>
                                    <div className="bg-gray-50 dark:bg-slate-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm">
                                        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-100 mb-4">
                                            Appeal Summary
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                                            <div>
                                                <p className="font-medium text-gray-500 dark:text-gray-300">Reasons</p>
                                                <p className="mt-1 text-gray-800 dark:text-gray-100">
                                                    {formData.reason?.join(", ") || "—"}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-500 dark:text-gray-300">Email</p>
                                                <p className="mt-1 text-gray-800 dark:text-gray-100">
                                                    {formData.email || "—"}
                                                </p>
                                            </div>
                                            <div className="md:col-span-2">
                                                <p className="font-medium text-gray-500 dark:text-gray-300">
                                                    Explanation
                                                </p>
                                                <p className="mt-1 text-gray-800 dark:text-gray-100 whitespace-pre-line">
                                                    {formData.message || "—"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                                        We aim to review all appeals within 3-5 business days.
                                        Confirmation will be sent by email after submission.
                                    </div>
                                    <div className="flex justify-between">
                                        <button
                                            type="button"
                                            onClick={prevStep}
                                            className="px-5 py-2 rounded-full text-sm font-medium text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                                        >
                                            ← Previous
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-6 py-2 rounded-full text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:opacity-90 transition"
                                        >
                                            {isLoading ? "Submitting..." : "Submit Appeal"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>
                </main>
                <Footer />
            </div>

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/40 animate-fadeIn">
                    <div
                        style={glassStyles}
                        className="p-8 max-w-sm w-full mx-4 animate-slideUp dark:bg-slate-800/80 dark:border-gray-700"
                    >
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 mb-4">
                                <FaRegCheckCircle className="text-green-500 text-2xl" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                                Submitted Successfully
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-300 mt-1">{sucessMessage}</p>
                            <p className="text-xs text-blue-500 dark:text-blue-300 mt-4 animate-pulse">
                                Redirecting...
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Animations */}
            <style>{`
        .animate-fadeIn { animation: fadeIn 0.4s ease-in; }
        .animate-slideUp { animation: slideUp 0.45s ease-out; }
        @keyframes fadeIn { from {opacity:0;} to {opacity:1;} }
        @keyframes slideUp { from {transform:translateY(20px);opacity:0;} to {transform:translateY(0);opacity:1;} }
      `}</style>
        </>
    );
};

export default Complain;
